import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Link } from 'react-router-dom';
import { Row, Col, Button, Form, Badge, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, ExternalLink, CheckCircle, AlertTriangle, HelpCircle, Info, Inbox, TrendingUp, TrendingDown, Minus, X, Medal, Phone, Search } from 'lucide-react';
import numeral from 'numeral';
import styled from 'styled-components';
import { AreaChart, Area, Tooltip, ResponsiveContainer } from 'recharts';

import PrivateLayout from '../../layouts/PrivateLayout';
import { useUser } from '../../../contexts/UserContext';
import { useFetchMyKpis, useFetchClaimsManagers } from '../../../services/myKpis';
import RcCallModal from '../../ringcentral/RcCallModal';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COLORS = {
  green: { solid: '#16a34a', light: 'rgba(22,163,74,0.08)', border: 'rgba(22,163,74,0.2)' },
  blue: { solid: '#2563eb', light: 'rgba(37,99,235,0.08)', border: 'rgba(37,99,235,0.2)' },
  amber: { solid: '#f59e0b', light: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)' },
  red: { solid: '#dc2626', light: 'rgba(220,38,38,0.08)', border: 'rgba(220,38,38,0.2)' },
  purple: { solid: '#8b5cf6', light: 'rgba(139,92,246,0.08)', border: 'rgba(139,92,246,0.2)' },
  slate: { solid: '#64748b', light: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.2)' },
};

// Map API status values to our color keys
const statusToColor = (s) => (s === 'yellow' ? 'amber' : s === 'gray' ? 'slate' : s);

const LABEL_STYLE = {
  fontSize: '0.65rem',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#64748b',
  marginBottom: 6,
};

const BIG_NUMBER_STYLE = {
  fontSize: 'clamp(1.25rem, 2.5vw, 2rem)',
  fontWeight: 300,
  lineHeight: 1.1,
  color: '#1e293b',
};

const SUBTEXT_STYLE = {
  fontSize: '0.8rem',
  color: '#94a3b8',
  marginTop: 2,
};

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const DashboardWrapper = styled.div`
  font-family: 'DM Sans', sans-serif;
`;

const KpiCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  height: 100%;
`;

const BreakdownSide = styled.div`
  display: none;
  @media (min-width: 1200px) {
    display: contents;
  }
`;

const TIER_STYLES = {
  Gold: {
    gradient: 'linear-gradient(135deg, #fef9c3 0%, #fde047 40%, #eab308 100%)',
    border: '1px solid rgba(202, 138, 4, 0.25)',
    label: '#854d0e',
    tier: '#854d0e',
    amount: '#713f12',
    sub: '#a16207',
  },
  Silver: {
    gradient: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 40%, #cbd5e1 100%)',
    border: '1px solid rgba(100, 116, 139, 0.25)',
    label: '#475569',
    tier: '#334155',
    amount: '#1e293b',
    sub: '#64748b',
  },
  Bronze: {
    gradient: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 40%, #d97706 100%)',
    border: '1px solid rgba(180, 83, 9, 0.25)',
    label: '#92400e',
    tier: '#92400e',
    amount: '#78350f',
    sub: '#a16207',
  },
};

const QUARTERLY_TIERS = [
  { tier: 'Bronze', threshold: 45, color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
  { tier: 'Silver', threshold: 60, color: '#94a3b8', bg: 'rgba(148,163,184,0.08)' },
  { tier: 'Gold', threshold: 90, color: '#eab308', bg: 'rgba(234,179,8,0.08)' },
];

const FadeUp = styled.div`
  opacity: 0;
  transform: translateY(12px);
  animation: kpiFadeUp 0.5s ease-out forwards;
  animation-delay: ${(props) => props.$delay || '0s'};
  position: relative;
  z-index: ${(props) => props.$zIndex || 0};

  @keyframes kpiFadeUp {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * Generic info icon with a fixed-position tooltip that stays in the viewport.
 */
const InfoTooltip = ({ color = '#64748b', children }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, flipUp: false });
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);
  const TOOLTIP_W = 280;

  const handleEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - TOOLTIP_W / 2;
      left = Math.max(8, Math.min(left, window.innerWidth - TOOLTIP_W - 8));
      // Initial position below — will correct after render if needed
      setPos({ top: rect.bottom + 6, left, flipUp: false });
    }
    setShow(true);
  };

  // After tooltip renders, measure actual height and flip if it goes off-screen
  useEffect(() => {
    if (show && tooltipRef.current && iconRef.current) {
      const tipRect = tooltipRef.current.getBoundingClientRect();
      if (tipRect.bottom > window.innerHeight - 8) {
        const iconRect = iconRef.current.getBoundingClientRect();
        setPos((prev) => ({ ...prev, top: iconRect.top - 6, flipUp: true }));
      }
    }
  }, [show]);

  return (
    <span
      ref={iconRef}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      style={{ display: 'inline-flex', cursor: 'pointer' }}
    >
      <Info size={14} style={{ color }} />
      {show && ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          style={{
            position: 'fixed',
            ...(pos.flipUp
              ? { bottom: window.innerHeight - pos.top }
              : { top: pos.top }),
            left: pos.left,
            width: TOOLTIP_W,
            background: '#fff',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 10,
            padding: '10px 14px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            fontSize: '0.75rem',
            zIndex: 9999,
            fontFamily: "'DM Sans', sans-serif",
            color: '#1e293b',
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </span>
  );
};

/**
 * Small Activity pulse icon that shows "last touch" info on hover.
 */
const TouchTooltip = ({ label, date }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const iconRef = useRef(null);

  const handleEnter = () => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      let left = rect.left + rect.width / 2 - 120;
      left = Math.max(8, Math.min(left, window.innerWidth - 248));
      setPos({ top: rect.bottom + 4, left });
    }
    setShow(true);
  };

  return (
    <span
      ref={iconRef}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      style={{ display: 'inline-flex', marginLeft: 4, cursor: 'pointer', verticalAlign: 'middle' }}
    >
      <Search size={12} style={{ color: '#94a3b8' }} />
      {show && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: 240,
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 8,
          padding: '8px 12px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          fontSize: '0.75rem',
          zIndex: 9999,
          fontFamily: "'DM Sans', sans-serif",
          color: '#1e293b',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 2, color: '#64748b', fontSize: '0.65rem' }}>Last Touch</div>
          <div style={{ marginBottom: 2 }}>{label}</div>
          <div style={{ color: '#94a3b8', fontSize: '0.7rem' }}>
            {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
};

const TierTooltip = ({ color = '#94a3b8', avgPerMonth, currentTier, multiplier = 1 }) => {
  const tierColor = currentTier === 'Gold' ? '#eab308'
    : currentTier === 'Silver' ? '#94a3b8'
    : currentTier === 'Bronze' ? '#d97706'
    : '#64748b';
  const fmt = (amt) => numeral(amt * multiplier).format('$0,0');
  return (
    <InfoTooltip color={color}>
      <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
        How This Works
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#eab308', flexShrink: 0 }} />
        <span><strong>Gold</strong> — 30+ avg/mo — {fmt(20000)}/qtr</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#94a3b8', flexShrink: 0 }} />
        <span><strong>Silver</strong> — 20+ avg/mo — {fmt(10000)}/qtr</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706', flexShrink: 0 }} />
        <span><strong>Bronze</strong> — 15+ avg/mo — {fmt(5000)}/qtr</span>
      </div>
      <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
      <div style={{ color: tierColor }}>
        Projected Avg: <strong>{avgPerMonth}</strong>/mo — <strong>{currentTier || 'Below threshold'}</strong>
      </div>
    </InfoTooltip>
  );
};

const PaceTooltip = ({ pace = {} }) => {
  const colorMap = { green: COLORS.green.solid, yellow: COLORS.amber.solid, red: COLORS.red.solid };
  const flagColor = colorMap[pace.consistencyColor] || COLORS.slate.solid;
  const TrendIcon = pace.consistencyTrend === 'up' ? TrendingUp
    : pace.consistencyTrend === 'down' ? TrendingDown : Minus;
  return (
    <InfoTooltip>
      <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
        How This Works
      </div>
      <div style={{ marginBottom: 6, color: '#475569' }}>
        Chart shows your monthly submission rate (90d, 60d, 30d).
      </div>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>
        Consistency
      </div>
      <div style={{ marginBottom: 3, color: '#475569', fontSize: '0.82rem' }}>
        Compares your last 30 days to your 90-day average:
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.green.solid, flexShrink: 0 }} />
        <span><strong>Stable</strong> — within 2 of your 90d avg</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.green.solid, flexShrink: 0 }} />
        <span><strong>Improving</strong> — below target but trending up</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.green.solid, flexShrink: 0 }} />
        <span><strong>Variable</strong> — 2–4 above your avg</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.amber.solid, flexShrink: 0 }} />
        <span><strong>Large Variable</strong> — 4+ above your avg</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.amber.solid, flexShrink: 0 }} />
        <span><strong>Declining</strong> — 2–4 below your avg</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.red.solid, flexShrink: 0 }} />
        <span><strong>Large Decline</strong> — 4+ below your avg</span>
      </div>
      <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
      <div style={{ color: flagColor }}>
        <div style={{ marginBottom: 3 }}>90d avg: <strong>{numeral((pace.last90Days || 0) / 3).format('0.[00]')}</strong>/mo</div>
        <div style={{ marginBottom: 3 }}>30d: <strong>{numeral(pace.last30Days || 0).format('0.[00]')}</strong>/mo</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          Consistency: <TrendIcon size={13} /> <strong>{pace.consistencyFlag || 'N/A'}</strong>
        </div>
      </div>
    </InfoTooltip>
  );
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

const useAnimatedNumber = (target, duration = 900) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target === null || target === undefined || target === 0) {
      setValue(0);
      return;
    }

    const startTime = performance.now();
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(target * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setValue(target);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return value;
};

// ---------------------------------------------------------------------------
// Small presentational components
// ---------------------------------------------------------------------------

/**
 * SVG circular progress ring with animated stroke.
 */
const ProgressRing = ({ value, size = 64, strokeWidth = 5, color = COLORS.green.solid }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(0,0,0,0.06)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          fontWeight: 600,
          color,
        }}
      >
        {Math.round(value)}%
      </div>
    </div>
  );
};

/**
 * Inline sparkline: tiny area chart with no axes, embedded in a card.
 */
const Sparkline = React.memo(({ data, color = '#2563eb', height = 40, gradientId = 'sparkFill' }) => {
  if (!data || data.length === 0) return null;

  const SparkTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const entry = payload[0]?.payload;
    return (
      <div
        style={{
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.1)',
          borderRadius: 6,
          padding: '4px 8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          fontSize: '0.75rem',
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <strong>{entry.month || entry.period}:</strong> {numeral(entry.value).format('0,0.[00]')}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 2 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0.05} />
          </linearGradient>
        </defs>
        <Tooltip content={<SparkTooltip />} isAnimationActive={false} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={{ r: 4, fill: color, stroke: '#fff', strokeWidth: 2 }}
          activeDot={{ r: 5, fill: color, stroke: '#fff', strokeWidth: 2 }}
          isAnimationActive={true}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
});

/**
 * Thin horizontal progress bar for queue health cards.
 */
const QueueProgressBar = ({ current, total, color }) => {
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  return (
    <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.06)', marginTop: 8 }}>
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          borderRadius: 3,
          background: color,
          transition: 'width 0.6s ease-out',
        }}
      />
    </div>
  );
};

/**
 * Status summary card with colored left border for quick scanning.
 */
const StatusSummaryCard = ({ status, icon: Icon, title, description, info }) => {
  const colorKey = statusToColor(status);
  const c = COLORS[colorKey] || COLORS.slate;

  return (
    <KpiCard style={{ borderLeft: `3px solid ${c.solid}`, display: 'flex', alignItems: 'center', gap: 12 }}>
      <Icon size={20} style={{ color: c.solid, flexShrink: 0 }} />
      <div>
        <div className="d-flex align-items-center gap-1" style={{ fontWeight: 500, color: c.solid }}>
          {title}
          {info}
        </div>
        <div style={{ ...SUBTEXT_STYLE, marginTop: 0 }}>{description}</div>
      </div>
    </KpiCard>
  );
};

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const SUBMISSION_TYPE_LABELS = {
  new_increase: 'New/Inc',
  supplemental: 'Supp',
  hlr: 'HLR',
  bdd: 'BDD',
};
const SUBMISSION_TYPE_COLORS = {
  new_increase: '#3b82f6',
  supplemental: '#8b5cf6',
  hlr: '#f59e0b',
  bdd: '#22c55e',
};

const MyKpiDashboard = () => {
  const { user, hasPermission } = useUser();
  const canViewAll = hasPermission('kpi:view_all');
  const [viewAs, setViewAs] = useState(null);

  // Submission breakdown toggle
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Pagination for Attention Required lists
  const [slaRiskPage, setSlaRiskPage] = useState(0);
  const [followupPage, setFollowupPage] = useState(0);
  const [submittedCheckinPage, setSubmittedCheckinPage] = useState(0);
  const [callTarget, setCallTarget] = useState(null);
  const PAGE_SIZE = 5;

  // Fetch claims managers list (for QA dropdown) - only for nic@
  const managersQuery = useFetchClaimsManagers({ enabled: canViewAll });

  // Fetch KPI data
  const { isLoading, data, error, refetch, isRefetching } = useFetchMyKpis(viewAs);

  // Reset pagination when switching users
  useEffect(() => {
    setSlaRiskPage(0);
    setFollowupPage(0);
    setSubmittedCheckinPage(0);
  }, [viewAs]);

  // Extract data — must be above all hooks and early returns
  const core = data?.coreMetrics || {};
  const queue = data?.queueHealth || {};
  const pace = data?.paceMetrics || {};

  // Memoize sparkline data to prevent new references during animated re-renders
  const paceSparkData = useMemo(
    () => [
      { period: '90d', value: Math.round(((pace.last90Days || 0) / 3) * 10) / 10 },
      { period: '60d', value: Math.round(((pace.last60Days || 0) / 2) * 10) / 10 },
      { period: '30d', value: pace.last30Days || 0 },
    ],
    [pace.last90Days, pace.last60Days, pace.last30Days],
  );

  // Animated numbers - must be called unconditionally (hooks rules)
  const animMtd = useAnimatedNumber(core.monthlyMtd || 0);
  const animEom = useAnimatedNumber(core.projectedEom || 0);
  const animQTotal = useAnimatedNumber(core.quarterlyTotal || 0);
  const animQAvg = useAnimatedNumber(core.quarterlyAverage || 0);
  const animQueueLoad = useAnimatedNumber(queue.queueLoad || 0);
  const animActive = useAnimatedNumber(queue.activeVeterans || 0);
  const animOldest = useAnimatedNumber(queue.oldestCaseAgeDays || 0);
  const animNoTouch14 = useAnimatedNumber(queue.noTouch14Days || 0);
  const animNoTouch30 = useAnimatedNumber(queue.noTouch30Days || 0);

  // Handle error state
  if (error && !data) {
    return (
      <PrivateLayout>
        <DashboardWrapper>
          <KpiCard>
            <h2 className="mb-3">My Performance Dashboard</h2>
            <Alert variant="danger">
              {error.response?.data?.msg || 'Failed to load dashboard'}
            </Alert>
          </KpiCard>
        </DashboardWrapper>
      </PrivateLayout>
    );
  }

  // Handle noAccess response (user has no assigned claims) — skeleton empty state
  if (data?.noAccess) {
    const SkeletonCard = ({ height = 170 }) => (
      <KpiCard style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.04)', minHeight: height }}>
        <div style={{ background: 'rgba(0,0,0,0.06)', width: '55%', height: 10, borderRadius: 4 }} />
        <div style={{ background: 'rgba(0,0,0,0.04)', width: '35%', height: 28, borderRadius: 4, marginTop: 16 }} />
        <div style={{ background: 'rgba(0,0,0,0.03)', width: '45%', height: 8, borderRadius: 4, marginTop: 12 }} />
        <div style={{ background: 'rgba(0,0,0,0.02)', width: '80%', height: 32, borderRadius: 4, marginTop: 16 }} />
      </KpiCard>
    );

    return (
      <PrivateLayout>
        <DashboardWrapper>
          {/* Header + QA dropdown */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div style={LABEL_STYLE}>MY PERFORMANCE DASHBOARD</div>
              <h3 style={{ fontWeight: 400, color: '#1e293b', margin: 0 }}>
                {viewAs && managersQuery.data?.managers
                  ? managersQuery.data.managers.find((m) => m.userId === viewAs)?.name || 'Loading...'
                  : `${user?.userFirstName} ${user?.userLastName}`}
              </h3>
            </div>
            {canViewAll && managersQuery.data?.managers && (
              <Form.Select
                size="sm"
                style={{ maxWidth: 200 }}
                value={viewAs || ''}
                onChange={(e) => setViewAs(e.target.value || null)}
              >
                <option value="">Select a manager...</option>
                {managersQuery.data.managers.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
              </Form.Select>
            )}
          </div>

          {/* Skeleton grid with overlay */}
          <div style={{ position: 'relative' }}>
            {/* Overlay message */}
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(255,255,255,0.45)',
              borderRadius: 14,
              zIndex: 2,
              pointerEvents: 'none',
            }}>
              <Inbox size={44} style={{ color: '#94a3b8', marginBottom: 12 }} />
              <div style={{ fontSize: '1.15rem', fontWeight: 500, color: '#64748b' }}>
                No assigned claims yet
              </div>
              <div style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: 4 }}>
                This dashboard activates when you have active claims in your queue
              </div>
            </div>

            {/* Skeleton cards */}
            <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
              <Col xs={6} md={3}><SkeletonCard /></Col>
              <Col xs={6} md={3}><SkeletonCard /></Col>
              <Col xs={6} md={3}><SkeletonCard /></Col>
              <Col xs={6} md={3}><SkeletonCard /></Col>
            </Row>
            <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
              <Col xs={12} md={4}><SkeletonCard height={200} /></Col>
              <Col xs={12} md={4}><SkeletonCard height={200} /></Col>
              <Col xs={12} md={4}><SkeletonCard height={200} /></Col>
            </Row>
            <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
              <Col xs={6} md={3}><SkeletonCard /></Col>
              <Col xs={6} md={3}><SkeletonCard /></Col>
              <Col xs={6} md={3}><SkeletonCard /></Col>
              <Col xs={6} md={3}><SkeletonCard /></Col>
            </Row>
          </div>
        </DashboardWrapper>
      </PrivateLayout>
    );
  }

  const manager = data?.manager || {};
  const attention = data?.attentionRequired || {};
  const status = data?.statusIndicators || {};

  const currentMonthName = new Date().toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  const paceTrendColor =
    paceSparkData[2]?.value >= paceSparkData[0]?.value ? COLORS.green.solid : COLORS.red.solid;

  // EOM difference from target
  const eomDiff = (core.projectedEom || 0) - (core.monthlyTarget || 15);

  // Oldest case severity color (full object for solid + border access)
  const oldestColorObj =
    queue.oldestCaseAgeDays >= 30
      ? COLORS.red
      : queue.oldestCaseAgeDays >= 15
        ? COLORS.amber
        : COLORS.green;
  const oldestColor = oldestColorObj.solid;

  return (
    <>
    <PrivateLayout>
      <DashboardWrapper>
        {/* Row 1: Header */}
        <FadeUp $delay="0s" $zIndex={6}>
          <Row className="mb-3 align-items-end">
            <Col>
              <div style={LABEL_STYLE}>My Performance Dashboard</div>
              <h3 style={{ fontWeight: 400, color: '#1e293b', marginBottom: 0 }}>
                {manager.name || 'Loading...'}
              </h3>
            </Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              <div className="text-end me-2 d-none d-md-block" style={{ whiteSpace: 'nowrap' }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#1e293b' }}>
                  {currentMonthName}
                </div>
                <div style={SUBTEXT_STYLE}>{core.daysRemaining || 0} days remaining</div>
              </div>
              {canViewAll && managersQuery.data?.managers && (
                <Form.Select
                  size="sm"
                  style={{ maxWidth: 200, fontSize: '0.8rem' }}
                  value={viewAs || ''}
                  onChange={(e) => setViewAs(e.target.value || null)}
                >
                  <option value="">View as: Myself</option>
                  {managersQuery.data.managers.map((m) => (
                    <option key={m.userId} value={m.userId}>
                      {m.name}
                    </option>
                  ))}
                </Form.Select>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => refetch()}
                disabled={isRefetching}
                style={{ borderRadius: 8, fontSize: '0.8rem' }}
              >
                {isRefetching ? <Spinner animation="border" size="sm" /> : 'Refresh'}
              </Button>
            </Col>
          </Row>
        </FadeUp>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <div className="mt-2" style={SUBTEXT_STYLE}>
              Loading your metrics...
            </div>
          </div>
        ) : (
          <>
            {/* Row 2: Core Metrics — 4 cards */}
            <FadeUp $delay="0.05s" $zIndex={5}>
              <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
                {/* This Month */}
                <Col xs={6} md={3}>
                  <KpiCard>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      This Month
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>How This Works</div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Submissions counted so far this calendar month vs. your monthly target.
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          The progress ring fills as you approach your target. Click the breakdown button to see submissions by type.
                        </div>
                      </InfoTooltip>
                    </div>
                    <div className="d-flex align-items-center justify-content-around" style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center" style={{ gap: 'clamp(0.4rem, 1.5vw, 0.75rem)', minWidth: 0 }}>
                        <ProgressRing value={core.monthlyProgressPct || 0} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...BIG_NUMBER_STYLE, whiteSpace: 'nowrap' }}>
                            {numeral(animMtd).format('0,0.[00]')}
                          </div>
                          <div style={{ ...SUBTEXT_STYLE, whiteSpace: 'nowrap' }}>of {core.monthlyTarget || 15} target</div>
                        </div>
                      </div>
                      {core.submissionBreakdown && Object.keys(core.submissionBreakdown).length > 0 && (
                        <BreakdownSide>
                          <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(0,0,0,0.08)' }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {Object.entries(core.submissionBreakdown)
                              .filter(([, count]) => count > 0)
                              .map(([type, count]) => (
                                <div key={type} className="d-flex align-items-center gap-1" style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                  <span style={{ fontWeight: 500, color: SUBMISSION_TYPE_COLORS[type] }}>
                                    {SUBMISSION_TYPE_LABELS[type]}:
                                  </span>
                                  <span style={{ fontWeight: 700, color: SUBMISSION_TYPE_COLORS[type] }}>
                                    {count}
                                  </span>
                                  {type === 'hlr' && (
                                    <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.65rem' }}>(x0.25)</span>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        </BreakdownSide>
                      )}
                    </div>
                    <button
                      onClick={() => setShowBreakdown(!showBreakdown)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        marginTop: 8,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        color: '#3b82f6',
                        cursor: 'pointer',
                      }}
                    >
                      View breakdown
                    </button>
                    {showBreakdown && ReactDOM.createPortal(
                      <div
                        onClick={() => setShowBreakdown(false)}
                        style={{
                          position: 'fixed',
                          inset: 0,
                          zIndex: 1050,
                          background: 'rgba(0,0,0,0.25)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            background: '#fff',
                            borderRadius: 16,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                            width: '90%',
                            maxWidth: 420,
                            maxHeight: '70vh',
                            display: 'flex',
                            flexDirection: 'column',
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                        >
                          <div className="d-flex align-items-center" style={{ padding: '1rem 1.25rem 0.5rem', position: 'relative' }}>
                            <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b', textAlign: 'center', flex: 1 }}>
                              This Month — Breakdown
                            </span>
                            <button
                              onClick={() => setShowBreakdown(false)}
                              style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#94a3b8', borderRadius: 6, position: 'absolute', right: '1.25rem' }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                          <div style={{ padding: '0 1.25rem 1rem', overflowY: 'auto' }}>
                            {core.submissionDetails?.length > 0 ? (
                              <>
                                <ListGroup variant="flush" style={{ fontSize: '0.75rem' }}>
                                  {(() => {
                                    // Group submissions by claimId
                                    const grouped = [];
                                    const seen = {};
                                    for (const item of core.submissionDetails) {
                                      if (!seen[item.claimId]) {
                                        seen[item.claimId] = { ...item, types: [item.type], totalPoints: item.points };
                                        grouped.push(seen[item.claimId]);
                                      } else {
                                        if (!seen[item.claimId].types.includes(item.type)) {
                                          seen[item.claimId].types.push(item.type);
                                        }
                                        seen[item.claimId].totalPoints += item.points;
                                      }
                                    }
                                    return grouped.map((item, i) => (
                                      <ListGroup.Item
                                        key={item.claimId}
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ padding: '0.5rem 0', border: 'none', borderTop: i > 0 ? '1px solid rgba(0,0,0,0.04)' : 'none' }}
                                      >
                                        <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flexWrap: 'wrap' }}>
                                          <span style={{ fontWeight: 500, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {item.veteranName}
                                          </span>
                                          {item.types.map((type) => (
                                            <span
                                              key={type}
                                              style={{
                                                fontSize: '0.65rem',
                                                fontWeight: 600,
                                                padding: '1px 6px',
                                                borderRadius: 4,
                                                background: `${SUBMISSION_TYPE_COLORS[type]}15`,
                                                color: SUBMISSION_TYPE_COLORS[type],
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {SUBMISSION_TYPE_LABELS[type]}
                                            </span>
                                          ))}
                                        </div>
                                        <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
                                          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#475569' }}>
                                            +{item.totalPoints % 1 === 0 ? item.totalPoints : item.totalPoints.toFixed(2)}
                                          </span>
                                          <Link
                                            to={`/admin/profile/${item.claimId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                              fontSize: '0.65rem',
                                              fontWeight: 500,
                                              padding: '1px 8px',
                                              borderRadius: 6,
                                              border: '1px solid rgba(0,0,0,0.12)',
                                              color: '#64748b',
                                              textDecoration: 'none',
                                            }}
                                          >
                                            View
                                          </Link>
                                        </div>
                                      </ListGroup.Item>
                                    ));
                                  })()}
                                </ListGroup>
                                {core.submissionBreakdown && (
                                  <div style={{ marginTop: 8, fontSize: '0.65rem', color: '#94a3b8', lineHeight: 1.6, textAlign: 'center' }}>
                                    {Object.entries(core.submissionBreakdown)
                                      .filter(([, count]) => count > 0)
                                      .map(([type, count]) => (
                                        <span key={type} style={{ marginRight: 10 }}>
                                          <span style={{ color: SUBMISSION_TYPE_COLORS[type], fontWeight: 600 }}>
                                            {SUBMISSION_TYPE_LABELS[type]}:
                                          </span>{' '}
                                          {count}{type === 'hlr' ? ' (x0.25)' : ''}
                                        </span>
                                      ))
                                    }
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{ fontSize: '0.75rem', color: '#94a3b8', padding: '0.5rem 0' }}>
                                No submissions this month
                              </div>
                            )}
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )}
                  </KpiCard>
                </Col>

                {/* Projected EOM */}
                <Col xs={6} md={3}>
                  <KpiCard style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      Projected EOM
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                          How This Works
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Current pace projected to end of month:
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          <strong>submissions so far / days elapsed × days in month</strong>
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          The sparkline shows your actual monthly totals for the last 6 months, with this month's projected value.
                        </div>
                        <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                        <div>
                          <div style={{ marginBottom: 3 }}>
                            <span style={{ color: eomDiff >= 0 ? COLORS.green.solid : COLORS.red.solid }}><strong>{core.monthlyMtd || 0}</strong> submissions / <strong>{core.daysElapsed || 0}</strong> days × <strong>{(core.daysElapsed || 0) + (core.daysRemaining || 0)}</strong> days = <strong>{numeral(core.projectedEom).format('0.[00]')}</strong></span>
                          </div>
                        </div>
                      </InfoTooltip>
                    </div>
                    <div style={BIG_NUMBER_STYLE}>
                      {numeral(animEom).format('0,0.[00]')}
                    </div>
                    <div
                      style={{
                        ...SUBTEXT_STYLE,
                        color: eomDiff >= 0 ? COLORS.green.solid : COLORS.red.solid,
                      }}
                    >
                      {eomDiff >= 0 ? '+' : ''}
                      {numeral(eomDiff).format('0.[00]')} {eomDiff >= 0 ? 'above' : 'below'} target
                    </div>
                    {core.monthlyHistory?.length > 0 && (
                      <div style={{ marginTop: 'auto' }}>
                        <Sparkline
                          data={core.monthlyHistory}
                          color={COLORS.blue.solid}
                          height={36}
                          gradientId="sparkFill-eom"
                        />
                      </div>
                    )}
                  </KpiCard>
                </Col>

                {/* Pace & Consistency */}
                <Col xs={6} md={3}>
                  <KpiCard style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      Pace & Consistency <PaceTooltip pace={pace} />
                    </div>
                    {(() => {
                      const cMap = { green: COLORS.green.solid, yellow: COLORS.amber.solid, red: COLORS.red.solid };
                      const fc = cMap[pace.consistencyColor] || COLORS.slate.solid;
                      const TIcon = pace.consistencyTrend === 'up' ? TrendingUp
                        : pace.consistencyTrend === 'down' ? TrendingDown : Minus;
                      return (
                        <div className="d-flex align-items-center gap-1">
                          <TIcon size={14} style={{ color: fc }} />
                          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: fc }}>
                            {pace.consistencyFlag || 'N/A'}
                          </span>
                        </div>
                      );
                    })()}
                    <div style={{ marginTop: 'auto' }}>
                      <Sparkline
                        data={paceSparkData}
                        color={paceTrendColor}
                        height={48}
                        gradientId="sparkFill-pace"
                      />
                    </div>
                  </KpiCard>
                </Col>

                {/* Quarterly Achievements */}
                <Col xs={6} md={3}>
                  <KpiCard style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="d-flex align-items-center gap-1" style={{ ...LABEL_STYLE, marginBottom: 8 }}>
                      Achievements
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>How This Works</div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          As your quarterly average submissions per month cross tier thresholds, you earn achievement badges toward your bonus.
                        </div>
                      </InfoTooltip>
                    </div>
                    <div className="d-flex align-items-center justify-content-around" style={{ flex: 1, width: '100%' }}>
                      {(() => {
                        return QUARTERLY_TIERS.map(({ tier, threshold, color }) => {
                          const earned = (core.quarterlyTotal || 0) >= threshold;
                          return (
                            <div key={tier} style={{ textAlign: 'center', transition: 'all 0.4s ease', opacity: earned ? 1 : 0.35 }}>
                              <Medal size={52} color={earned ? color : '#cbd5e1'} strokeWidth={1} style={{ filter: earned ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' : 'none' }} />
                              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: earned ? color : '#b0b8c4', marginTop: 5, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                {tier}
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </KpiCard>
                </Col>
              </Row>
            </FadeUp>

            {/* Row 3: Quarterly — 2 cards */}
            <FadeUp $delay="0.1s" $zIndex={4}>
              <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
                {/* Quarterly Overview */}
                <Col xs={12} md={6}>
                  <KpiCard>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      Quarterly Overview
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>How This Works</div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Total submission points this quarter, monthly average, and projected total.
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Projection = actual so far + (projected EOM × months remaining in quarter).
                        </div>
                        <div style={{ color: '#475569' }}>
                          The progress bar shows distance to the next bonus tier threshold (Bronze 45, Silver 60, Gold 90 quarterly total).
                        </div>
                      </InfoTooltip>
                    </div>
                    <div className="d-flex justify-content-between" style={{ marginTop: 4 }}>
                      {[
                        { label: 'Total', value: numeral(animQTotal).format('0,0.[00]'), color: '#1a1a2e' },
                        { label: 'Monthly Avg', value: numeral(animQAvg).format('0,0.[00]'), color: '#1a1a2e' },
                        { label: 'Projected', value: numeral(core.projectedQuarterlyTotal || 0).format('0,0.[00]'), color: '#3b82f6' },
                      ].map((item, i, arr) => (
                        <div key={i} style={{
                          flex: 1,
                          textAlign: 'center',
                          borderRight: i < arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                          padding: '0 8px',
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: '#94a3b8', marginBottom: 4 }}>
                            {item.label}
                          </div>
                          <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 26, fontWeight: 700, lineHeight: 1.1, color: item.color }}>
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Multi-segment tier progress bar */}
                    {(() => {
                      const total = core.quarterlyTotal || 0;
                      const maxTier = QUARTERLY_TIERS[QUARTERLY_TIERS.length - 1];
                      const barMax = Math.max(total, maxTier.threshold);
                      // Determine current and next tier
                      const earnedTiers = QUARTERLY_TIERS.filter((t) => total >= t.threshold);
                      const nextTier = QUARTERLY_TIERS.find((t) => total < t.threshold);
                      const currentTierName = earnedTiers.length > 0 ? earnedTiers[earnedTiers.length - 1].tier : null;
                      const fillPct = Math.min((total / barMax) * 100, 100);
                      // Fill color matches the highest earned tier (or blue if none)
                      const fillColor = currentTierName
                        ? QUARTERLY_TIERS.find((t) => t.tier === currentTierName).color
                        : '#3b82f6';

                      return (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                            <span>
                              {numeral(total).format('0,0.[00]')}
                              {nextTier && <span style={{ color: '#94a3b8' }}> / {nextTier.threshold}</span>}
                            </span>
                            <span style={{ color: nextTier ? nextTier.color : fillColor }}>
                              {nextTier ? `Next: ${nextTier.tier}` : 'Gold Achieved'}
                            </span>
                          </div>
                          {/* Bar with tier zone backgrounds */}
                          <div style={{ position: 'relative', height: 8, borderRadius: 4, overflow: 'visible' }}>
                            {/* Zone backgrounds */}
                            <div style={{ display: 'flex', height: '100%', borderRadius: 4, overflow: 'hidden' }}>
                              {QUARTERLY_TIERS.map((t, i) => {
                                const prev = i > 0 ? QUARTERLY_TIERS[i - 1].threshold : 0;
                                const width = ((t.threshold - prev) / barMax) * 100;
                                return (
                                  <div key={t.tier} style={{ width: `${width}%`, background: t.bg, borderRight: i < QUARTERLY_TIERS.length - 1 ? '2px solid #fff' : 'none' }} />
                                );
                              })}
                              {/* Overflow zone past Gold */}
                              {total > maxTier.threshold && (
                                <div style={{ flex: 1, background: 'rgba(234,179,8,0.04)' }} />
                              )}
                            </div>
                            {/* Fill bar */}
                            <div
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                height: '100%',
                                width: `${fillPct}%`,
                                borderRadius: 4,
                                background: fillColor,
                                transition: 'width 0.6s ease',
                                opacity: 0.85,
                              }}
                            />
                            {/* Tier threshold ticks */}
                            {QUARTERLY_TIERS.slice(0, -1).map((t) => (
                              <div
                                key={t.tier}
                                style={{
                                  position: 'absolute',
                                  left: `${(t.threshold / barMax) * 100}%`,
                                  top: -2,
                                  width: 2,
                                  height: 12,
                                  background: total >= t.threshold ? t.color : '#cbd5e1',
                                  borderRadius: 1,
                                  transition: 'background 0.3s ease',
                                }}
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </KpiCard>
                </Col>

                {/* Projected Quarterly Bonus */}
                <Col xs={12} md={6}>
                  <KpiCard>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                        <strong>Projected</strong> Quarterly Bonus
                        <InfoTooltip color="#64748b">
                          <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                            How Projections Work
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.green.solid, flexShrink: 0, marginTop: 5 }} />
                            <span><strong>Past months</strong> — actual totals</span>
                          </div>
                          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.blue.solid, flexShrink: 0, marginTop: 5 }} />
                            <span><strong>Current/future</strong> — blends your pace with prior months, stabilizing as the month progresses</span>
                          </div>
                          <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                          <div style={{ color: '#475569', fontSize: '0.72rem' }}>
                            <div>
                              Projected quarterly avg: <strong>{numeral(core.projectedQuarterlyAverage || 0).format('0,0.[00]')}</strong>/mo
                              <div style={{ color: '#94a3b8', fontSize: '0.68rem', marginTop: 1 }}>Tier and bonus are based on this average</div>
                            </div>
                          </div>
                        </InfoTooltip>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <TierTooltip color="#64748b" avgPerMonth={numeral(core.projectedQuarterlyAverage || 0).format('0.[00]')} currentTier={core.projectedQuarterlyBonus?.tier} multiplier={data?.manager?.bonusMultiplier || 1} />
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                          Avg{' '}
                          <strong style={{ color: '#1e293b' }}>
                            {numeral(core.projectedQuarterlyAverage || 0).format('0,0.[00]')}
                          </strong>
                          /mo
                        </span>
                        {core.projectedQuarterlyBonus?.tier ? (
                          (() => {
                            const qts = TIER_STYLES[core.projectedQuarterlyBonus.tier] || TIER_STYLES.Bronze;
                            return (
                              <Badge
                                bg=""
                                style={{
                                  background: qts.gradient,
                                  color: qts.tier,
                                  border: qts.border,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                }}
                              >
                                {core.projectedQuarterlyBonus.tier}
                              </Badge>
                            );
                          })()
                        ) : (
                          <Badge
                            bg=""
                            style={{
                              background: COLORS.slate.light,
                              color: COLORS.slate.solid,
                              border: `1px solid ${COLORS.slate.border}`,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                            }}
                          >
                            Not Eligible
                          </Badge>
                        )}
                        <span
                          style={{
                            fontWeight: 600,
                            color: core.projectedQuarterlyBonus?.tier
                              ? COLORS.green.solid
                              : '#94a3b8',
                          }}
                        >
                          $
                          {numeral(
                            (core.projectedQuarterlyBonus?.amount || 0),
                          ).format('0,0')}
                        </span>
                      </div>
                    </div>

                    {/* Month tiles */}
                    <div className="d-flex gap-2">
                      {(core.quarterBreakdown || []).map((m, idx) => {
                        const value = m.value || 0;
                        const tileColor =
                          value >= 15
                            ? COLORS.green
                            : value >= 13
                              ? COLORS.amber
                              : COLORS.red;
                        const isComplete = m.type === 'actual';
                        const metTarget = value >= (core.monthlyTarget || 15);
                        return (
                          <div
                            key={idx}
                            className="flex-fill text-center"
                            style={{
                              padding: '10px 8px',
                              borderRadius: 10,
                              border: isComplete ? `3px solid ${tileColor.border}` : m.type === 'projected' ? '2px dashed rgba(59,130,246,0.45)' : '2px dashed rgba(0,0,0,0.1)',
                              position: 'relative',
                            }}
                          >
                            {isComplete && (
                              <div style={{ position: 'absolute', top: 4, right: 6 }}>
                                {metTarget
                                  ? <CheckCircle size={12} color={COLORS.green.solid} strokeWidth={2.5} />
                                  : <X size={12} color={COLORS.red.solid} strokeWidth={2.5} />
                                }
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                letterSpacing: '0.06em',
                                color: '#64748b',
                                textTransform: 'uppercase',
                              }}
                            >
                              {m.name}
                            </div>
                            <div
                              style={{
                                fontSize: '1.25rem',
                                fontWeight: 300,
                                color: tileColor.solid,
                                lineHeight: 1.3,
                              }}
                            >
                              {numeral(value).format('0,0.[00]')}
                            </div>
                            <div
                              style={{
                                fontSize: '0.6rem',
                                color: '#94a3b8',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                              }}
                            >
                              {isComplete
                                ? 'actual'
                                : m.type === 'projected'
                                  ? 'proj'
                                  : 'est'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </KpiCard>
                </Col>
              </Row>
            </FadeUp>

            {/* Row 4: Queue Health — 4 cards */}
            <FadeUp $delay="0.15s" $zIndex={3}>
              <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
                {/* Queue Load */}
                <Col xs={6} md={3}>
                  <KpiCard style={{ display: 'flex', flexDirection: 'column' }}>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      Queue Load
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                          How This Works
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Total veterans and weighted load across your active queue. Submitted claims count as x0.5 weight.
                        </div>
                        <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                        <div style={{ fontWeight: 600, marginBottom: 3, color: '#64748b', fontSize: '0.65rem' }}>Tracks:</div>
                        <div style={{ color: '#475569' }}>In Process, Awaiting Review, Review Approved, Submitted (x0.5)</div>
                      </InfoTooltip>
                    </div>
                    <div className="d-flex justify-content-around align-items-center" style={{ flex: 1, marginTop: 4 }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>
                          {numeral(animActive).format('0,0')}
                        </div>
                        <div style={SUBTEXT_STYLE}>veterans</div>
                      </div>
                      <div style={{ width: 1, height: 36, background: 'rgba(0,0,0,0.08)' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#64748b', lineHeight: 1 }}>
                          {numeral(animQueueLoad).format('0,0.[00]')}
                        </div>
                        <div style={SUBTEXT_STYLE}>weighted</div>
                      </div>
                    </div>
                    {queue.statusBreakdown && Object.keys(queue.statusBreakdown).length > 0 && (
                      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {[
                          { key: 'in process', label: 'In Process', color: '#2563eb' },
                          { key: 'awaiting internal review', label: 'Awaiting Review', color: '#f59e0b' },
                          { key: 'internal review approved', label: 'Review Approved', color: '#16a34a' },
                          { key: 'submitted to va', label: 'Submitted', color: '#8b5cf6' },
                        ]
                          .filter(({ key }) => queue.statusBreakdown[key] > 0)
                          .map(({ key, label, color }) => (
                            <div
                              key={key}
                              className="d-flex justify-content-between align-items-center"
                              style={{
                                fontSize: '0.65rem',
                                padding: '3px 8px',
                                borderRadius: 6,
                                background: `${color}0a`,
                              }}
                            >
                              <span style={{ fontWeight: 500, color }}>
                                {label}
                                {key === 'submitted to va' && (
                                  <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 3 }}>x0.5</span>
                                )}
                              </span>
                              <span style={{ fontWeight: 700, color }}>
                                {queue.statusBreakdown[key]}
                              </span>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </KpiCard>
                </Col>

                {/* No Touch 14–29 */}
                <Col xs={6} md={3}>
                  <KpiCard>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      No Touch 14–29 Days
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                          How This Works
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Veterans in your queue where no activity has been logged in the last 14 days. These cases may need a follow-up to stay on track.
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          A "touch" is any logged activity on the claim or file upload
                        </div>
                        <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                        <div style={{ color: (queue.noTouch14Days || 0) === 0 ? COLORS.green.solid : COLORS.amber.solid, marginBottom: 6 }}>
                          Untouched 14–29 days: <strong>{queue.noTouch14Days || 0}</strong> of <strong>{queue.activeVeterans || 0}</strong> veterans
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 3, color: '#64748b', fontSize: '0.65rem' }}>Tracks:</div>
                        <div style={{ color: '#475569' }}>In Process, Awaiting Review, Review Approved</div>
                      </InfoTooltip>
                    </div>
                    <div
                      style={{
                        ...BIG_NUMBER_STYLE,
                        color:
                          queue.noTouch14Days > 0 ? COLORS.amber.solid : COLORS.green.solid,
                      }}
                    >
                      {numeral(animNoTouch14).format('0,0')}
                    </div>
                    <div style={SUBTEXT_STYLE}>veterans</div>
                    <QueueProgressBar
                      current={queue.noTouch14Days || 0}
                      total={queue.activeVeterans || 1}
                      color={COLORS.amber.solid}
                    />
                  </KpiCard>
                </Col>

                {/* No Touch 30+ */}
                <Col xs={6} md={3}>
                  <KpiCard>
                    <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                      No Touch 30+ Days
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                          How This Works
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Veterans in your queue where no activity has been logged in the last 30 days. These cases are at SLA risk and should be prioritized.
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          A "touch" is any logged activity on the claim or file upload
                        </div>
                        <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                        <div style={{ color: (queue.noTouch30Days || 0) === 0 ? COLORS.green.solid : COLORS.red.solid, marginBottom: 6 }}>
                          Untouched 30+ days: <strong>{queue.noTouch30Days || 0}</strong> of <strong>{queue.activeVeterans || 0}</strong> veterans
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 3, color: '#64748b', fontSize: '0.65rem' }}>Tracks:</div>
                        <div style={{ color: '#475569' }}>In Process, Awaiting Review, Review Approved</div>
                      </InfoTooltip>
                    </div>
                    <div
                      style={{
                        ...BIG_NUMBER_STYLE,
                        color:
                          queue.noTouch30Days > 0 ? COLORS.red.solid : COLORS.green.solid,
                      }}
                    >
                      {numeral(animNoTouch30).format('0,0')}
                    </div>
                    <div style={SUBTEXT_STYLE}>veterans (SLA risk)</div>
                    <QueueProgressBar
                      current={queue.noTouch30Days || 0}
                      total={queue.activeVeterans || 1}
                      color={COLORS.red.solid}
                    />
                  </KpiCard>
                </Col>

                {/* Oldest Case */}
                <Col xs={6} md={3}>
                  <KpiCard style={{ border: `3px solid ${oldestColorObj.border}` }}>
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="d-flex align-items-center gap-1" style={LABEL_STYLE}>
                        Oldest Case
                        <InfoTooltip>
                          <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                            How This Works
                          </div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            Age of the oldest case in your queue by when it entered In Process. Falls back to creation date if no status history exists. Cases 30+ days old need attention.
                          </div>
                          <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                          <div style={{ fontWeight: 600, marginBottom: 3, color: '#64748b', fontSize: '0.65rem' }}>Tracks:</div>
                          <div style={{ color: '#475569' }}>In Process, Awaiting Review, Review Approved, Submitted</div>
                        </InfoTooltip>
                      </div>
                      {queue.oldestCaseClaimId && (
                        <Link
                          to={`/admin/profile/${queue.oldestCaseClaimId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#94a3b8', opacity: 0.6, transition: 'opacity 0.15s' }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = 1;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = 0.6;
                          }}
                        >
                          <ExternalLink size={14} />
                        </Link>
                      )}
                    </div>
                    <div style={{ ...BIG_NUMBER_STYLE, color: oldestColor }}>
                      {numeral(animOldest).format('0,0')}
                    </div>
                    <div style={SUBTEXT_STYLE}>
                      days{queue.oldestCaseAgeDays >= 30 ? <> – <em>needs attention</em></> : queue.oldestCaseAgeDays >= 15 ? <> – <em>may need attention</em></> : ''}
                    </div>
                    {queue.oldestCaseVeteranName && (
                      <div style={{ marginTop: 6, fontSize: '0.7rem', fontWeight: 500, color: '#475569' }}>
                        {queue.oldestCaseVeteranName}
                      </div>
                    )}
                    {queue.oldestCaseClaimId && (
                      <div style={{ marginTop: 4, fontSize: '0.6rem', color: '#94a3b8', fontStyle: 'italic' }}>
                        {queue.oldestCaseUsedFallback ? 'by creation date' : 'by in-process date'}
                      </div>
                    )}
                  </KpiCard>
                </Col>
              </Row>
            </FadeUp>

            {/* Row 5: Attention Required — 3 cards */}
            <FadeUp $delay="0.2s" $zIndex={2}>
              <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }} className="mb-3">
                {/* Needs Follow-up */}
                <Col xs={12} md={4}>
                  <KpiCard style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{ padding: '1rem 1.25rem 0.75rem' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: attention.veteransNeedingFollowup?.length > 0 ? COLORS.amber.solid : COLORS.green.solid,
                            display: 'inline-block',
                          }}
                        />
                        <span style={{ fontWeight: 600, color: attention.veteransNeedingFollowup?.length > 0 ? COLORS.amber.solid : COLORS.green.solid }}>
                          Needs Follow-up (14–29 days)
                        </span>
                        <InfoTooltip>
                          <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>How This Works</div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            Veterans in your queue where no activity has been logged in the last 14 days. These cases may need a follow-up to stay on track.
                          </div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            A "touch" is any logged activity on the claim or file upload
                          </div>
                          <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                          <div style={{ color: (attention.veteransNeedingFollowup?.length || 0) === 0 ? COLORS.green.solid : COLORS.amber.solid, marginBottom: 6 }}>
                            Untouched 14–29 days: <strong>{attention.veteransNeedingFollowup?.length || 0}</strong> of <strong>{queue.activeVeterans || 0}</strong> veterans
                          </div>
                          <div style={{ fontWeight: 600, marginBottom: 3, color: '#64748b', fontSize: '0.65rem' }}>Tracks:</div>
                          <div style={{ color: '#475569' }}>In Process, Awaiting Review, Review Approved</div>
                        </InfoTooltip>
                      </div>
                      <Badge
                        bg=""
                        style={{
                          background: attention.veteransNeedingFollowup?.length > 0 ? COLORS.amber.light : COLORS.green.light,
                          color: attention.veteransNeedingFollowup?.length > 0 ? COLORS.amber.solid : COLORS.green.solid,
                          border: `1px solid ${attention.veteransNeedingFollowup?.length > 0 ? COLORS.amber.border : COLORS.green.border}`,
                          fontWeight: 600,
                          borderRadius: 20,
                          padding: '3px 10px',
                        }}
                      >
                        {attention.veteransNeedingFollowup?.length || 0}
                      </Badge>
                    </div>
                    {attention.veteransNeedingFollowup?.length > 0 ? (
                      <>
                        <div style={{ flex: 1 }}>
                        <ListGroup variant="flush">
                          {attention.veteransNeedingFollowup
                            .slice(
                              followupPage * PAGE_SIZE,
                              (followupPage + 1) * PAGE_SIZE,
                            )
                            .map((item) => (
                              <ListGroup.Item
                                key={item.claimId}
                                className="d-flex justify-content-between align-items-center"
                                style={{ padding: '0.6rem 1.25rem', border: 'none', borderTop: '1px solid rgba(0,0,0,0.04)' }}
                              >
                                <span style={{ fontWeight: 500, color: '#1e293b' }}>
                                  {item.veteranName}
                                  {item.lastTouchLabel && (
                                    <TouchTooltip label={item.lastTouchLabel} date={item.lastTouchDate} />
                                  )}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    style={{
                                      fontSize: '0.8rem',
                                      fontWeight: 600,
                                      color: COLORS.amber.solid,
                                    }}
                                  >
                                    {item.daysSinceTouch}d
                                  </span>
                                  <Phone
                                    size={14}
                                    style={{
                                      color: item.userPhone ? '#94a3b8' : '#e2e8f0',
                                      cursor: item.userPhone ? 'pointer' : 'default',
                                      transition: 'color 0.15s, fill 0.15s',
                                      flexShrink: 0,
                                    }}
                                    fill="none"
                                    onClick={() => item.userPhone && setCallTarget(item.userPhone)}
                                    onMouseEnter={(e) => {
                                      if (item.userPhone) {
                                        e.currentTarget.style.color = '#475569';
                                        e.currentTarget.setAttribute('fill', 'currentColor');
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = item.userPhone ? '#94a3b8' : '#e2e8f0';
                                      e.currentTarget.setAttribute('fill', 'none');
                                    }}
                                  />
                                  <Link
                                    to={`/admin/profile/${item.claimId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      padding: '2px 10px',
                                      borderRadius: 6,
                                      border: '1px solid rgba(0,0,0,0.12)',
                                      color: '#64748b',
                                      textDecoration: 'none',
                                    }}
                                  >
                                    View
                                  </Link>
                                </div>
                              </ListGroup.Item>
                            ))}
                        </ListGroup>
                        </div>
                        {attention.veteransNeedingFollowup.length > PAGE_SIZE && (
                          <div
                            className="d-flex justify-content-between align-items-center"
                            style={{ padding: '0.5rem 1.25rem', borderTop: '1px solid rgba(0,0,0,0.04)' }}
                          >
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={followupPage === 0}
                              onClick={() => setFollowupPage((p) => p - 1)}
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
                            >
                              <ChevronLeft size={14} />
                            </Button>
                            <span
                              style={{
                                ...SUBTEXT_STYLE,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {followupPage * PAGE_SIZE + 1}-
                              {Math.min(
                                (followupPage + 1) * PAGE_SIZE,
                                attention.veteransNeedingFollowup.length,
                              )}{' '}
                              of {attention.veteransNeedingFollowup.length}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={
                                (followupPage + 1) * PAGE_SIZE >=
                                attention.veteransNeedingFollowup.length
                              }
                              onClick={() => setFollowupPage((p) => p + 1)}
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
                            >
                              <ChevronRight size={14} />
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ padding: '2rem 1.25rem', color: COLORS.green.solid }}
                      >
                        <CheckCircle size={28} style={{ marginBottom: 8, opacity: 0.7 }} />
                        <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>All clear, nice work!</span>
                      </div>
                    )}
                  </KpiCard>
                </Col>

                {/* SLA Risk */}
                <Col xs={12} md={4}>
                  <KpiCard style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{ padding: '1rem 1.25rem 0.75rem' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: attention.casesApproachingSlaRisk?.length > 0 ? COLORS.red.solid : COLORS.green.solid,
                            display: 'inline-block',
                          }}
                        />
                        <span style={{ fontWeight: 600, color: attention.casesApproachingSlaRisk?.length > 0 ? COLORS.red.solid : COLORS.green.solid }}>
                          SLA Risk (30+ days)
                        </span>
                        <InfoTooltip>
                          <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>How This Works</div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            Veterans in your queue where no activity has been logged in the last 30 days. These cases are at SLA risk and should be prioritized.
                          </div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            A "touch" is any logged activity on the claim or file upload
                          </div>
                          <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                          <div style={{ color: (attention.casesApproachingSlaRisk?.length || 0) === 0 ? COLORS.green.solid : COLORS.red.solid, marginBottom: 6 }}>
                            Untouched 30+ days: <strong>{attention.casesApproachingSlaRisk?.length || 0}</strong> of <strong>{queue.activeVeterans || 0}</strong> veterans
                          </div>
                          <div style={{ fontWeight: 600, marginBottom: 3, color: '#64748b', fontSize: '0.65rem' }}>Tracks:</div>
                          <div style={{ color: '#475569' }}>In Process, Awaiting Review, Review Approved</div>
                        </InfoTooltip>
                      </div>
                      <Badge
                        bg=""
                        style={{
                          background: attention.casesApproachingSlaRisk?.length > 0 ? COLORS.red.light : COLORS.green.light,
                          color: attention.casesApproachingSlaRisk?.length > 0 ? COLORS.red.solid : COLORS.green.solid,
                          border: `1px solid ${attention.casesApproachingSlaRisk?.length > 0 ? COLORS.red.border : COLORS.green.border}`,
                          fontWeight: 600,
                          borderRadius: 20,
                          padding: '3px 10px',
                        }}
                      >
                        {attention.casesApproachingSlaRisk?.length || 0}
                      </Badge>
                    </div>
                    {attention.casesApproachingSlaRisk?.length > 0 ? (
                      <>
                        <div style={{ flex: 1 }}>
                        <ListGroup variant="flush">
                          {attention.casesApproachingSlaRisk
                            .slice(slaRiskPage * PAGE_SIZE, (slaRiskPage + 1) * PAGE_SIZE)
                            .map((item) => (
                              <ListGroup.Item
                                key={item.claimId}
                                className="d-flex justify-content-between align-items-center"
                                style={{ padding: '0.6rem 1.25rem', border: 'none', borderTop: '1px solid rgba(0,0,0,0.04)' }}
                              >
                                <span style={{ fontWeight: 500, color: '#1e293b' }}>
                                  {item.veteranName}
                                  {item.lastTouchLabel && (
                                    <TouchTooltip label={item.lastTouchLabel} date={item.lastTouchDate} />
                                  )}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    style={{
                                      fontSize: '0.8rem',
                                      fontWeight: 600,
                                      color: COLORS.red.solid,
                                    }}
                                  >
                                    {item.daysSinceTouch}d
                                  </span>
                                  <Phone
                                    size={14}
                                    style={{
                                      color: item.userPhone ? '#94a3b8' : '#e2e8f0',
                                      cursor: item.userPhone ? 'pointer' : 'default',
                                      transition: 'color 0.15s, fill 0.15s',
                                      flexShrink: 0,
                                    }}
                                    fill="none"
                                    onClick={() => item.userPhone && setCallTarget(item.userPhone)}
                                    onMouseEnter={(e) => {
                                      if (item.userPhone) {
                                        e.currentTarget.style.color = '#475569';
                                        e.currentTarget.setAttribute('fill', 'currentColor');
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = item.userPhone ? '#94a3b8' : '#e2e8f0';
                                      e.currentTarget.setAttribute('fill', 'none');
                                    }}
                                  />
                                  <Link
                                    to={`/admin/profile/${item.claimId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      padding: '2px 10px',
                                      borderRadius: 6,
                                      border: '1px solid rgba(0,0,0,0.12)',
                                      color: '#64748b',
                                      textDecoration: 'none',
                                    }}
                                  >
                                    View
                                  </Link>
                                </div>
                              </ListGroup.Item>
                            ))}
                        </ListGroup>
                        </div>
                        {attention.casesApproachingSlaRisk.length > PAGE_SIZE && (
                          <div
                            className="d-flex justify-content-between align-items-center"
                            style={{ padding: '0.5rem 1.25rem', borderTop: '1px solid rgba(0,0,0,0.04)' }}
                          >
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={slaRiskPage === 0}
                              onClick={() => setSlaRiskPage((p) => p - 1)}
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
                            >
                              <ChevronLeft size={14} />
                            </Button>
                            <span
                              style={{
                                ...SUBTEXT_STYLE,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {slaRiskPage * PAGE_SIZE + 1}-
                              {Math.min(
                                (slaRiskPage + 1) * PAGE_SIZE,
                                attention.casesApproachingSlaRisk.length,
                              )}{' '}
                              of {attention.casesApproachingSlaRisk.length}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={
                                (slaRiskPage + 1) * PAGE_SIZE >=
                                attention.casesApproachingSlaRisk.length
                              }
                              onClick={() => setSlaRiskPage((p) => p + 1)}
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
                            >
                              <ChevronRight size={14} />
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ padding: '2rem 1.25rem', color: COLORS.green.solid }}
                      >
                        <CheckCircle size={28} style={{ marginBottom: 8, opacity: 0.7 }} />
                        <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>All clear, nice work!</span>
                      </div>
                    )}
                  </KpiCard>
                </Col>

                {/* Submitted Check-in */}
                <Col xs={12} md={4}>
                  <KpiCard style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div
                      className="d-flex justify-content-between align-items-center"
                      style={{ padding: '1rem 1.25rem 0.75rem' }}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: attention.submittedNeedingCheckin?.length > 0 ? COLORS.purple.solid : COLORS.green.solid,
                            display: 'inline-block',
                          }}
                        />
                        <span style={{ fontWeight: 600, color: attention.submittedNeedingCheckin?.length > 0 ? COLORS.purple.solid : COLORS.green.solid }}>
                          Submitted Follow-up (30+ days)
                        </span>
                        <InfoTooltip>
                          <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>How This Works</div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            Claims submitted to the VA with no activity in 30+ days. A monthly white-glove check-in is recommended for these veterans.
                          </div>
                          <div style={{ color: '#475569', marginBottom: 6 }}>
                            Any update on the claim counts as activity.
                          </div>
                          <div style={{ color: '#94a3b8', fontSize: '0.68rem' }}>Tracked separately from no-touch counts since submitted claims have different expectations.</div>
                        </InfoTooltip>
                      </div>
                      <Badge
                        bg=""
                        style={{
                          background: attention.submittedNeedingCheckin?.length > 0 ? COLORS.purple.light : COLORS.green.light,
                          color: attention.submittedNeedingCheckin?.length > 0 ? COLORS.purple.solid : COLORS.green.solid,
                          border: `1px solid ${attention.submittedNeedingCheckin?.length > 0 ? COLORS.purple.border : COLORS.green.border}`,
                          fontWeight: 600,
                          borderRadius: 20,
                          padding: '3px 10px',
                        }}
                      >
                        {attention.submittedNeedingCheckin?.length || 0}
                      </Badge>
                    </div>
                    {attention.submittedNeedingCheckin?.length > 0 ? (
                      <>
                        <div style={{ flex: 1 }}>
                        <ListGroup variant="flush">
                          {attention.submittedNeedingCheckin
                            .slice(
                              submittedCheckinPage * PAGE_SIZE,
                              (submittedCheckinPage + 1) * PAGE_SIZE,
                            )
                            .map((item) => (
                              <ListGroup.Item
                                key={item.claimId}
                                className="d-flex justify-content-between align-items-center"
                                style={{ padding: '0.6rem 1.25rem', border: 'none', borderTop: '1px solid rgba(0,0,0,0.04)' }}
                              >
                                <span style={{ fontWeight: 500, color: '#1e293b' }}>
                                  {item.veteranName}
                                  {item.lastTouchLabel && (
                                    <TouchTooltip label={item.lastTouchLabel} date={item.lastTouchDate} />
                                  )}
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    style={{
                                      fontSize: '0.8rem',
                                      fontWeight: 600,
                                      color: COLORS.purple.solid,
                                    }}
                                  >
                                    {item.daysSinceTouch}d
                                  </span>
                                  <Phone
                                    size={14}
                                    style={{
                                      color: item.userPhone ? '#94a3b8' : '#e2e8f0',
                                      cursor: item.userPhone ? 'pointer' : 'default',
                                      transition: 'color 0.15s, fill 0.15s',
                                      flexShrink: 0,
                                    }}
                                    fill="none"
                                    onClick={() => item.userPhone && setCallTarget(item.userPhone)}
                                    onMouseEnter={(e) => {
                                      if (item.userPhone) {
                                        e.currentTarget.style.color = '#475569';
                                        e.currentTarget.setAttribute('fill', 'currentColor');
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = item.userPhone ? '#94a3b8' : '#e2e8f0';
                                      e.currentTarget.setAttribute('fill', 'none');
                                    }}
                                  />
                                  <Link
                                    to={`/admin/profile/${item.claimId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      fontSize: '0.75rem',
                                      fontWeight: 500,
                                      padding: '2px 10px',
                                      borderRadius: 6,
                                      border: '1px solid rgba(0,0,0,0.12)',
                                      color: '#64748b',
                                      textDecoration: 'none',
                                    }}
                                  >
                                    View
                                  </Link>
                                </div>
                              </ListGroup.Item>
                            ))}
                        </ListGroup>
                        </div>
                        {attention.submittedNeedingCheckin.length > PAGE_SIZE && (
                          <div
                            className="d-flex justify-content-between align-items-center"
                            style={{ padding: '0.5rem 1.25rem', borderTop: '1px solid rgba(0,0,0,0.04)' }}
                          >
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={submittedCheckinPage === 0}
                              onClick={() => setSubmittedCheckinPage((p) => p - 1)}
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
                            >
                              <ChevronLeft size={14} />
                            </Button>
                            <span
                              style={{
                                ...SUBTEXT_STYLE,
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {submittedCheckinPage * PAGE_SIZE + 1}-
                              {Math.min(
                                (submittedCheckinPage + 1) * PAGE_SIZE,
                                attention.submittedNeedingCheckin.length,
                              )}{' '}
                              of {attention.submittedNeedingCheckin.length}
                            </span>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              disabled={
                                (submittedCheckinPage + 1) * PAGE_SIZE >=
                                attention.submittedNeedingCheckin.length
                              }
                              onClick={() => setSubmittedCheckinPage((p) => p + 1)}
                              className="d-flex align-items-center justify-content-center"
                              style={{ width: 28, height: 28, padding: 0, borderRadius: 6 }}
                            >
                              <ChevronRight size={14} />
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ padding: '2rem 1.25rem', color: COLORS.green.solid }}
                      >
                        <CheckCircle size={28} style={{ marginBottom: 8, opacity: 0.7 }} />
                        <span style={{ fontWeight: 500, fontSize: '0.85rem' }}>All clear, nice work!</span>
                      </div>
                    )}
                  </KpiCard>
                </Col>
              </Row>
            </FadeUp>

            {/* Row 6: Status Summary — 3 cards */}
            <FadeUp $delay="0.25s" $zIndex={1}>
              <Row style={{ '--bs-gutter-x': '12px', '--bs-gutter-y': '12px' }}>
                <Col xs={12} md={4}>
                  <StatusSummaryCard
                    status={status.submissionPace}
                    icon={
                      status.submissionPace === 'green' ? CheckCircle : AlertTriangle
                    }
                    title="Submission Pace"
                    info={
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                          How This Works
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Based on projected end-of-month submissions:
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.green.solid, flexShrink: 0 }} />
                          <span><strong>Green</strong> — 15+ projected (on target)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.amber.solid, flexShrink: 0 }} />
                          <span><strong>Yellow</strong> — 13–14 projected (slightly behind)</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.red.solid, flexShrink: 0 }} />
                          <span><strong>Red</strong> — below 13 projected</span>
                        </div>
                        <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                        <div style={{ color: (core.projectedEom || 0) >= 15 ? COLORS.green.solid : (core.projectedEom || 0) >= 13 ? COLORS.amber.solid : COLORS.red.solid }}>
                          Projected EOM: <strong>{numeral(core.projectedEom).format('0.[00]')}</strong>
                        </div>
                      </InfoTooltip>
                    }
                    description={
                      status.submissionPace === 'green'
                        ? 'On track for target'
                        : status.submissionPace === 'yellow'
                          ? 'Slightly behind pace'
                          : 'Below target pace'
                    }
                  />
                </Col>
                <Col xs={12} md={4}>
                  <StatusSummaryCard
                    status={status.queueHealth}
                    icon={
                      status.queueHealth === 'green' ? CheckCircle : AlertTriangle
                    }
                    title="Queue Health"
                    info={
                      <InfoTooltip>
                        <div style={{ fontWeight: 600, marginBottom: 6, textAlign: 'center' }}>
                          How This Works
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.7rem' }}>
                          Oldest Case
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.green.solid, flexShrink: 0 }} />
                          <span><strong>Green</strong> — 14 days or less</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.amber.solid, flexShrink: 0 }} />
                          <span><strong>Yellow</strong> — 15–29 days</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORS.red.solid, flexShrink: 0 }} />
                          <span><strong>Red</strong> — 30+ days</span>
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: '0.7rem' }}>
                          No Touch 30+ Days
                        </div>
                        <div style={{ color: '#475569', marginBottom: 6 }}>
                          Any case with no logged activity in 30+ days turns this red.
                        </div>
                        <div style={{ width: '40%', height: 1, background: 'rgba(0,0,0,0.1)', marginBottom: 6, marginLeft: 'auto', marginRight: 'auto' }} />
                        <div>
                          <div style={{ marginBottom: 3, color: (queue.oldestCaseAgeDays || 0) <= 14 ? COLORS.green.solid : (queue.oldestCaseAgeDays || 0) <= 29 ? COLORS.amber.solid : COLORS.red.solid }}>
                            Oldest Case: <strong>{queue.oldestCaseAgeDays || 0}</strong> days
                          </div>
                          <div style={{ color: (queue.noTouch30Days || 0) === 0 ? COLORS.green.solid : COLORS.red.solid }}>
                            No Touch 30+ Days: <strong>{queue.noTouch30Days || 0}</strong>
                          </div>
                        </div>
                      </InfoTooltip>
                    }
                    description={
                      status.queueHealth === 'green'
                        ? 'All cases healthy'
                        : status.queueHealth === 'yellow'
                          ? 'Some cases aging'
                          : `${(queue.noTouch30Days || 0) + (queue.submittedCheckinDue || 0)} cases need attention`
                    }
                  />
                </Col>
                <Col xs={12} md={4}>
                  <StatusSummaryCard
                    status="gray"
                    icon={HelpCircle}
                    title="CSAT"
                    description="Coming soon"
                  />
                </Col>
              </Row>
            </FadeUp>
          </>
        )}
      </DashboardWrapper>
    </PrivateLayout>
    <RcCallModal
      show={!!callTarget}
      onHide={() => setCallTarget(null)}
      targetNumber={callTarget || ''}
    />
  </>
  );
};

export default MyKpiDashboard;
